import React, { type ComponentPropsWithRef, type ComponentType } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type RouteObject, useNavigate } from "react-router-dom-v5-compat";
import type { AppDispatch, TripProduct } from "@b2bportal/core-types";
import { mapQueryResponse } from "@b2bportal/core-utilities";
import { Product } from "@b2bportal/purchase-api";
import {
  type ActivityReservation,
  ItineraryState,
} from "@b2bportal/rbc-activities-api";
import {
  ActivityReservationCard,
  ActivityReservationPageRoute,
} from "@tenant/activities";
import { activitiesPostBookingApi } from "../store";

export type ActivityTripProduct = TripProduct<ActivityReservation>;

export const useActivityTripProduct = ({
  tripsPath,
  reservationPath,
}: {
  tripsPath: string;
  reservationPath: (reservationId: string) => string;
}): {
  activities: { [Product.Activities]: ActivityTripProduct };
  pastActivities: { [Product.Activities]: ActivityTripProduct };
  activityReservationRouteProps: RouteObject;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const product = React.useMemo(
    () => ({
      fetch: () => {
        dispatch(
          activitiesPostBookingApi.endpoints.fetchAllActivityReservations.initiate()
        );
      },
      component: ({
        reservation,
        ...props
      }: ComponentPropsWithRef<
        ComponentType<{ reservation: ActivityReservation }>
      >) => (
        <ActivityReservationCard
          reservation={reservation}
          selectItinerary={() =>
            navigate(reservationPath(reservation.customerReservationId))
          }
          {...props}
        />
      ),
    }),
    [dispatch, navigate, reservationPath]
  );

  const reservations = useSelector(
    activitiesPostBookingApi.endpoints.fetchAllActivityReservations.select()
  );

  return React.useMemo(
    () => ({
      activities: {
        [Product.Activities]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data.itineraries[ItineraryState.Future] ?? []),
            ...(data.itineraries[ItineraryState.Present] ?? []),
          ]),
        },
      },
      pastActivities: {
        [Product.Activities]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data.itineraries[ItineraryState.Past] ?? []),
            ...(data.itineraries[ItineraryState.Canceled] ?? []),
          ]),
        },
      },
      activityReservationRouteProps: {
        path: reservationPath(":reservationId"),
        element: <ActivityReservationPageRoute />,
      },
    }),
    [product, reservationPath, reservations]
  );
};
